import React from 'react';
import { Box, CardContent, styled, keyframes } from '@mui/material';
import { Card, Typography } from './index';

// Stat card configuration interface
export interface StatCardProps {
  title: string;
  subtitle?: string;
  value: string | number;
  percentage?: number;
  percentageLabel?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  color?: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary';
  valuePrefix?: string;
  valueSuffix?: string;
  loading?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'outlined' | 'gradient';
  animationDelay?: number; // Delay for staggered animations (in milliseconds)
  disableAnimation?: boolean; // Option to disable animations
  customStyles?: {
    card?: React.CSSProperties;
    content?: React.CSSProperties;
    title?: React.CSSProperties;
    subtitle?: React.CSSProperties;
    value?: React.CSSProperties;
    percentage?: React.CSSProperties;
    icon?: React.CSSProperties;
  };
}

// Color theme mappings
const COLOR_THEMES = {
  success: {
    percentage: '#4caf50',
    icon: '#4caf50',
    gradient: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
  },
  error: {
    percentage: '#f44336',
    icon: '#f44336',
    gradient: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
  },
  warning: {
    percentage: '#ff9800',
    icon: '#ff9800',
    gradient: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
  },
  info: {
    percentage: '#2196f3',
    icon: '#2196f3',
    gradient: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
  },
  primary: {
    percentage: '#1976d2',
    icon: '#1976d2',
    gradient: 'linear-gradient(135deg, #1976d2 0%, #1e88e5 100%)',
  },
  secondary: {
    percentage: '#9c27b0',
    icon: '#9c27b0',
    gradient: 'linear-gradient(135deg, #9c27b0 0%, #ab47bc 100%)',
  },
};

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Size configurations
const SIZE_CONFIGS = {
  small: {
    padding: '8px',
    titleFontSize: '0.875rem',
    valueFontSize: '1rem',
    iconSize: 24,
  },
  medium: {
    padding: '10px',
    titleFontSize: '1rem',
    valueFontSize: '1.2rem',
    iconSize: 30,
  },
  large: {
    padding: '16px',
    titleFontSize: '1.125rem',
    valueFontSize: '1.5rem',
    iconSize: 36,
  },
};

// Styled components
const StyledCard = styled(Card, {
  shouldForwardProp: prop =>
    !['statVariant', 'statColor', 'clickable', 'animationDelay', 'disableAnimation'].includes(
      prop as string
    ),
})<{
  statVariant?: string;
  statColor?: string;
  clickable?: boolean;
  animationDelay?: number;
  disableAnimation?: boolean;
}>(
  ({ theme, statVariant, statColor, clickable, animationDelay = 0, disableAnimation = false }) => ({
    borderRadius: 12,
    background:
      statVariant === 'gradient' && statColor
        ? COLOR_THEMES[statColor as keyof typeof COLOR_THEMES]?.gradient || '#fff'
        : '#fff',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.03)',
    border: statVariant === 'outlined' ? `1px solid ${theme.palette.divider}` : 'none',
    cursor: clickable ? 'pointer' : 'default',
    transition: 'all 0.2s ease-in-out',
    // Page load animations with configurable delay
    animation: disableAnimation ? 'none' : `${fadeInUp} 0.6s ease-out ${animationDelay}ms both`,
    '&:hover': clickable
      ? {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
          transform: 'translateY(-1px)',
        }
      : {},
  })
);

const ContentWrapper = styled(CardContent)<{
  size?: string;
  animationDelay?: number;
  disableAnimation?: boolean;
}>(({ size = 'medium', animationDelay = 0, disableAnimation = false }) => {
  const config = SIZE_CONFIGS[size as keyof typeof SIZE_CONFIGS];
  return {
    padding: `${config.padding} !important`,
    animation: disableAnimation
      ? 'none'
      : `${scaleIn} 0.7s ease-out ${animationDelay + 100}ms both`,
  };
});

const IconContainer = styled(Box)<{
  size?: string;
  iconColor?: string;
  animationDelay?: number;
  disableAnimation?: boolean;
}>(({ theme, size = 'medium', iconColor, animationDelay = 0, disableAnimation = false }) => {
  const config = SIZE_CONFIGS[size as keyof typeof SIZE_CONFIGS];
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: disableAnimation
      ? 'none'
      : `${slideInFromLeft} 0.8s ease-out ${animationDelay + 200}ms both`,
    '& > *': {
      fontSize: `${config.iconSize}px !important`,
      marginTop: -10,
      color: `${iconColor || theme.palette.primary.main} !important`,
    },
    '& svg': {
      fontSize: `${config.iconSize}px !important`,
      color: `${iconColor || theme.palette.primary.main} !important`,
    },
    '& .MuiSvgIcon-root': {
      fontSize: `${config.iconSize}px !important`,
      color: `${iconColor || theme.palette.primary.main} !important`,
    },
  };
});

const PercentageText = styled(Typography)<{ trendColor?: string }>(({ trendColor }) => ({
  color: trendColor || '#4caf50',
  fontWeight: 600,
  fontSize: '0.8rem',
}));

// Loading skeleton
const LoadingSkeleton: React.FC<{ size?: string }> = ({ size = 'medium' }) => {
  const config = SIZE_CONFIGS[size as keyof typeof SIZE_CONFIGS];

  return (
    <ContentWrapper size={size}>
      <Box
        sx={{
          width: '60%',
          height: config.titleFontSize,
          bgcolor: 'grey.200',
          borderRadius: 1,
          mb: 1,
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.4 },
            '100%': { opacity: 1 },
          },
        }}
      />
      <Box
        sx={{
          width: '40%',
          height: '0.8rem',
          bgcolor: 'grey.100',
          borderRadius: 1,
          mb: 1,
        }}
      />
      <Box
        sx={{
          width: '80%',
          height: config.valueFontSize,
          bgcolor: 'grey.200',
          borderRadius: 1,
          mb: 1,
        }}
      />
      <Box
        sx={{
          width: '50%',
          height: '0.8rem',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      />
    </ContentWrapper>
  );
};

export const StatCard: React.FC<StatCardProps> = ({
  title,
  subtitle,
  value,
  percentage,
  percentageLabel,
  icon,
  trend = 'neutral',
  color = 'primary',
  valuePrefix = '',
  valueSuffix = '',
  loading = false,
  onClick,
  size = 'medium',
  variant = 'default',
  animationDelay = 0,
  disableAnimation = false,
  customStyles = {},
}) => {
  const sizeConfig = SIZE_CONFIGS[size];
  const colorTheme = COLOR_THEMES[color];
  const isClickable = !!onClick;

  // Determine trend color
  const getTrendColor = () => {
    if (trend === 'up') return colorTheme.percentage;
    if (trend === 'down') return COLOR_THEMES.error.percentage;
    return colorTheme.percentage;
  };

  if (loading) {
    return (
      <StyledCard
        statVariant={variant}
        statColor={color}
        animationDelay={animationDelay}
        disableAnimation={disableAnimation}
        sx={customStyles.card}
      >
        <LoadingSkeleton size={size} />
      </StyledCard>
    );
  }

  return (
    <StyledCard
      statVariant={variant}
      statColor={color}
      clickable={isClickable}
      onClick={onClick}
      animationDelay={animationDelay}
      disableAnimation={disableAnimation}
      sx={customStyles.card}
    >
      <ContentWrapper
        size={size}
        animationDelay={animationDelay}
        disableAnimation={disableAnimation}
        sx={customStyles.content}
      >
        {/* Title */}
        <Typography
          variant="body1"
          sx={{
            fontSize: sizeConfig.titleFontSize,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            mb: 0,
            color: variant === 'gradient' ? 'white' : 'text.primary',
            animation: disableAnimation
              ? 'none'
              : `${slideInFromLeft} 0.9s ease-out ${animationDelay + 300}ms both`,
            ...customStyles.title,
          }}
        >
          {title}
        </Typography>

        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.8rem',
              opacity: variant === 'gradient' ? 0.9 : 0.7,
              mb: 0.25,
              color: variant === 'gradient' ? 'rgba(255,255,255,0.8)' : 'text.secondary',
              animation: disableAnimation
                ? 'none'
                : `${slideInFromLeft} 1s ease-out ${animationDelay + 350}ms both`,
              ...customStyles.subtitle,
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Value */}
        <Typography
          variant="h4"
          sx={{
            fontSize: sizeConfig.valueFontSize,
            fontWeight: 600,
            mb: 0.5,
            color: variant === 'gradient' ? 'white' : 'text.primary',
            animation: disableAnimation
              ? 'none'
              : `${scaleIn} 0.8s ease-out ${animationDelay + 400}ms both`,
            ...customStyles.value,
          }}
        >
          {valuePrefix}
          {value}
          {valueSuffix}
        </Typography>

        {/* Bottom row with percentage and icon */}
        <Box display="flex" alignItems="center">
          {/* Percentage/Status */}
          <PercentageText
            variant="body2"
            trendColor={variant === 'gradient' ? 'rgba(255,255,255,0.9)' : getTrendColor()}
            sx={{
              animation: disableAnimation
                ? 'none'
                : `${slideInFromLeft} 1.1s ease-out ${animationDelay + 450}ms both`,
              ...customStyles.percentage,
            }}
          >
            {percentage !== undefined
              ? `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`
              : percentageLabel || ''}
          </PercentageText>

          <Box sx={{ flexGrow: 1 }} />

          {/* Icon */}
          {icon && (
            <IconContainer
              size={size}
              iconColor={variant === 'gradient' ? 'rgba(255,255,255,0.9)' : colorTheme.icon}
              animationDelay={animationDelay}
              disableAnimation={disableAnimation}
              sx={{
                ...customStyles.icon,
                '& > *': {
                  fontSize: `${sizeConfig.iconSize}px !important`,
                  color: `${variant === 'gradient' ? 'rgba(255,255,255,0.9)' : colorTheme.icon} !important`,
                  marginTop: -10,
                },
              }}
            >
              {icon}
            </IconContainer>
          )}
        </Box>
      </ContentWrapper>
    </StyledCard>
  );
};

export default StatCard;
